/**
 * Frame profiler (`?debug=perf`). Subsystems wrap their work in `perf.begin(label)` /
 * `perf.end(label)` (a no-op branch while disabled). Per frame it keeps ms per label, the frame's
 * total CPU, renderer stats (draw calls, triangles, programs) and the JS heap, and shows
 * p50/p95/p99 over the last few seconds in an overlay. `window.__perf` exposes the raw series.
 *
 * Labels nest freely (`render`, `render.units`, ...): each span is timed on its own, so a
 * parent's time includes its children.
 */

export interface PerfRendererStats {
  drawCalls: number;
  triangles: number;
  programs?: number;
  textures?: number;
  geometries?: number;
}

export interface PerfSeriesStats {
  p50: number;
  p95: number;
  p99: number;
  max: number;
  mean: number;
  n: number;
}

const HISTORY = 3600;

export function pctl(sorted: ArrayLike<number>, p: number): number {
  const n = sorted.length;
  if (n === 0) return NaN;
  const i = Math.min(n - 1, Math.max(0, Math.ceil((p / 100) * n) - 1));
  return sorted[i]!;
}

class Ring {
  readonly buf = new Float64Array(HISTORY);
  n = 0;
  head = 0;
  push(v: number): void {
    this.buf[this.head] = v;
    this.head = (this.head + 1) % HISTORY;
    if (this.n < HISTORY) this.n++;
  }
  values(): number[] {
    const out: number[] = new Array(this.n);
    for (let i = 0; i < this.n; i++) out[i] = this.buf[(this.head - this.n + i + HISTORY) % HISTORY]!;
    return out;
  }
  clear(): void {
    this.n = 0;
    this.head = 0;
  }
}

function seriesStats(values: number[]): PerfSeriesStats {
  const s = values.slice().sort((a, b) => a - b);
  const mean = s.length ? s.reduce((a, b) => a + b, 0) / s.length : NaN;
  return { p50: pctl(s, 50), p95: pctl(s, 95), p99: pctl(s, 99), max: s.length ? s[s.length - 1]! : NaN, mean, n: s.length };
}

export class PerfProbe {
  enabled = false;
  /** Open span start times by label. */
  private open = new Map<string, number>();
  /** This frame's ms by label. */
  private cur = new Map<string, number>();
  private series = new Map<string, Ring>();
  private frameT0 = 0;
  private inFrame = false;
  private lastHeap = 0;
  private overlay: HTMLElement | null = null;
  private lastOverlayAt = 0;
  /** Latest renderer stats (set by the renderer each frame). */
  renderer: PerfRendererStats | null = null;
  /** Extra text lines for the overlay (quality scaler state, ...). */
  notes: Record<string, string> = {};
  /** Debug handles the renderer registers for experiments from the console / CDP benchmark. */
  handles: Record<string, unknown> = {};
  /** Heap drops seen (a GC ran) and bytes allocated (sum of heap growth) since reset. */
  gcCount = 0;
  allocBytes = 0;
  private allocT0 = 0;

  begin(label: string): void {
    if (!this.enabled) return;
    this.open.set(label, performance.now());
  }

  end(label: string): void {
    if (!this.enabled) return;
    const t = this.open.get(label);
    if (t === undefined) return;
    this.open.delete(label);
    this.cur.set(label, (this.cur.get(label) ?? 0) + performance.now() - t);
  }

  /** Time `f` under `label`. */
  time<T>(label: string, f: () => T): T {
    if (!this.enabled) return f();
    const t = performance.now();
    try {
      return f();
    } finally {
      this.cur.set(label, (this.cur.get(label) ?? 0) + performance.now() - t);
    }
  }

  /** Add an already measured duration. */
  add(label: string, ms: number): void {
    if (!this.enabled) return;
    this.cur.set(label, (this.cur.get(label) ?? 0) + ms);
  }

  frameBegin(now = performance.now()): void {
    if (!this.enabled) return;
    this.frameT0 = now;
    this.inFrame = true;
  }

  /** Close the frame: `frame` = CPU ms from frameBegin, plus every label (0 when absent). */
  frameEnd(dtMs: number): void {
    if (!this.enabled || !this.inFrame) return;
    this.inFrame = false;
    const now = performance.now();
    this.cur.set('frame', now - this.frameT0);
    this.cur.set('dt', dtMs);
    const r = this.renderer;
    if (r) {
      this.cur.set('drawCalls', r.drawCalls);
      this.cur.set('triangles', r.triangles);
      if (r.programs !== undefined) this.cur.set('programs', r.programs);
      if (r.textures !== undefined) this.cur.set('textures', r.textures);
      if (r.geometries !== undefined) this.cur.set('geometries', r.geometries);
    }
    const mem = (performance as unknown as { memory?: { usedJSHeapSize: number } }).memory;
    if (mem) {
      const h = mem.usedJSHeapSize;
      if (this.lastHeap) {
        if (h < this.lastHeap) this.gcCount++;
        else this.allocBytes += h - this.lastHeap;
      }
      this.lastHeap = h;
    }
    for (const k of this.series.keys()) if (!this.cur.has(k)) this.cur.set(k, 0);
    for (const [k, v] of this.cur) {
      let s = this.series.get(k);
      if (!s) {
        s = new Ring();
        this.series.set(k, s);
      }
      s.push(v);
    }
    this.cur.clear();
    if (this.overlay && now - this.lastOverlayAt > 500) {
      this.lastOverlayAt = now;
      this.overlay.textContent = this.report();
    }
  }

  summary(): Record<string, PerfSeriesStats> {
    const out: Record<string, PerfSeriesStats> = {};
    for (const [k, s] of this.series) out[k] = seriesStats(s.values());
    return out;
  }

  /** Allocation rate (MB/s) since reset (heap growth between GCs; a lower bound). */
  allocRate(): number {
    const secs = (performance.now() - this.allocT0) / 1000;
    return secs > 0 ? this.allocBytes / 1e6 / secs : 0;
  }

  reset(): void {
    for (const s of this.series.values()) s.clear();
    this.gcCount = 0;
    this.allocBytes = 0;
    this.allocT0 = performance.now();
    this.lastHeap = 0;
  }

  report(): string {
    const s = this.summary();
    const f = (v: number | undefined, w = 6, d = 2): string => (v !== undefined && Number.isFinite(v) ? v.toFixed(d).padStart(w) : '-'.padStart(w));
    const timing = Object.keys(s)
      .filter((k) => !['dt', 'drawCalls', 'triangles', 'programs', 'textures', 'geometries'].includes(k))
      .sort((a, b) => (a === 'frame' ? -1 : b === 'frame' ? 1 : a < b ? -1 : 1));
    const lines = ['perf ms          p50    p95    p99    max'];
    for (const k of timing) lines.push(`${k.padEnd(14)} ${f(s[k]!.p50)} ${f(s[k]!.p95)} ${f(s[k]!.p99)} ${f(s[k]!.max)}`);
    const dt = s['dt'];
    if (dt) lines.push(`frame dt       ${f(dt.p50)} ${f(dt.p95)} ${f(dt.p99)} ${f(dt.max)}  (${(1000 / dt.p50).toFixed(0)} fps)`);
    const r = this.renderer;
    if (r) lines.push(`draws ${r.drawCalls}  tris ${(r.triangles / 1000).toFixed(0)}k  programs ${r.programs ?? '-'}`);
    lines.push(`gc ${this.gcCount}  alloc ${this.allocRate().toFixed(1)} MB/s`);
    for (const [k, v] of Object.entries(this.notes)) lines.push(`${k}: ${v}`);
    return lines.join('\n');
  }

  attachOverlay(host: HTMLElement): void {
    if (this.overlay || typeof document === 'undefined') return;
    const o = document.createElement('div');
    o.style.cssText = 'position:fixed;top:6px;right:6px;z-index:9999;font:11px/1.3 ui-monospace,monospace;color:#dfe;background:rgba(0,0,0,0.72);padding:4px 8px;border-radius:4px;pointer-events:none;white-space:pre';
    host.appendChild(o);
    this.overlay = o;
  }

  dispose(): void {
    this.overlay?.remove();
    this.overlay = null;
    this.enabled = false;
  }
}

/** The one profiler; subsystems import it and call begin/end unconditionally. */
export const perf = new PerfProbe();

/** True when the page was opened with `?debug=perf` (or a list containing it). */
export function perfRequested(search: string): boolean {
  const v = new URLSearchParams(search).get('debug');
  return !!v && v.split(',').includes('perf');
}
